import React, { useState, useEffect } from 'react'
import { Wrench, Database, HardDrive, AlertTriangle, CheckCircle, Trash2, RefreshCw, Shield, Copy, HelpCircle, Play, Calendar, Search } from 'lucide-react'
import toast from 'react-hot-toast'

// Tooltip Component
const Tooltip = ({ children, text, maxWidth = 'max-w-xs' }) => {
  return (
    <div className="relative group">
      {children}
      <div className={`absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ${maxWidth} bottom-full left-1/2 transform -translate-x-1/2 mb-2`}>
        {text}
        <div className="absolute w-2 h-2 bg-gray-900 rotate-45 top-full left-1/2 transform -translate-x-1/2 -mt-1"></div>
      </div>
    </div>
  )
}

const MaintenanceTab = () => {
  const [loading, setLoading] = useState(false)
  const [systemHealth, setSystemHealth] = useState(null)
  const [storageStats, setStorageStats] = useState(null)
  const [orphanData, setOrphanData] = useState(null)
  const [allVideos, setAllVideos] = useState([])
  const [r2DebugData, setR2DebugData] = useState(null)
  const [showR2Debug, setShowR2Debug] = useState(false)
  const [storageAnalysis, setStorageAnalysis] = useState(null)
  const [showStorageAnalysis, setShowStorageAnalysis] = useState(false)
  const [analysisLoading, setAnalysisLoading] = useState(false)

  useEffect(() => {
    checkSystemHealth()
  }, [])

  const checkSystemHealth = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')

      // Run health checks in parallel
      const [systemHealth, orphanCheck, videosData] = await Promise.all([
        fetch('/.netlify/functions/admin-system-health', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json()),

        fetch('/.netlify/functions/cleanup-multipart-orphans', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json()),

        fetch('/.netlify/functions/admin-videos', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json())
      ])

      setSystemHealth({
        databaseVideos: systemHealth.analysis?.totalVideos || 0,
        r2Videos: systemHealth.storage?.totalObjects || 0,
        syncStatus: systemHealth.analysis?.totalVideos === systemHealth.storage?.totalObjects ? 'synced' : 'mismatch',
        encryptedVideos: systemHealth.analysis?.encryptedVideos || 0,
        unencryptedVideos: systemHealth.analysis?.unencryptedVideos || 0
      })

      setStorageStats({
        totalFiles: systemHealth.storage?.totalObjects || 0,
        totalSize: systemHealth.storage?.totalSizeGB || '0',
        totalSizeBytes: systemHealth.storage?.totalSizeBytes || 0,
        allObjects: systemHealth.storage?.allObjects || []
      })

      setOrphanData({
        multipartOrphans: orphanCheck.cleaned || 0,
        hasOrphans: orphanCheck.message?.includes('found') === false || orphanCheck.cleaned > 0
      })

      // Process videos with file sizes
      if (videosData.success) {
        const videosWithSize = videosData.videos.map(video => {
          // Find matching R2 file
          const r2File = systemHealth.storage?.allObjects?.find(obj =>
            obj.key.includes(video.cloudflare_uid)
          )

          return {
            ...video,
            fileSize: r2File?.size || 0,
            filePath: r2File?.key || null,
            hasFile: !!r2File,
            lastModified: r2File?.lastModified || video.created_at
          }
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Most recent first

        setAllVideos(videosWithSize)
      }

      toast.success('System health check complete')
    } catch (error) {
      console.error('Health check failed:', error)
      toast.error('Health check failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copied!`)
    } catch (error) {
      toast.error(`Failed to copy ${label}`)
    }
  }

  const deleteVideo = async (video) => {
    const fileSize = video.fileSize || 0

    if (!confirm(`⚠️ DELETE "${video.title}"?\n\nThis will permanently delete:\n• Video file (${formatBytes(fileSize)})\n• Database record\n\nThis CANNOT be undone!`)) {
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/video-delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ videoId: video.id })
      })

      const result = await response.json()
      if (result.success) {
        toast.success(`✅ Deleted: ${video.title}`)
        checkSystemHealth() // Refresh all data
      } else {
        toast.error(result.error || 'Deletion failed')
      }
    } catch (error) {
      console.error('Deletion failed:', error)
      toast.error('Deletion failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const cleanOrphanUploads = async () => {
    if (!orphanData?.hasOrphans) {
      toast.info('✅ No orphaned data found - system is clean!')
      return
    }

    if (!confirm(`Clean up orphaned upload data?\n\nThis will free up storage space by removing incomplete uploads that failed to complete properly.`)) {
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/cleanup-multipart-orphans', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (result.success) {
        toast.success(`✅ Cleaned up orphaned data`)
        checkSystemHealth() // Refresh data
      } else {
        toast.error(result.error || 'Cleanup failed')
      }
    } catch (error) {
      console.error('Cleanup failed:', error)
      toast.error('Cleanup failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const debugR2Storage = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/debug-r2-list', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()

      if (data.success) {
        setR2DebugData(data)
        setShowR2Debug(true)
        toast.success('R2 storage structure loaded')
        console.log('R2 Storage Structure:', data) // Still log to console for advanced debugging
      } else {
        toast.error('Failed to load R2 debug data')
      }
    } catch (error) {
      console.error('Debug R2 error:', error)
      toast.error('Debug R2 failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const analyzeStorage = async () => {
    setAnalysisLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/admin-storage-analysis', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.success) {
        setStorageAnalysis(data.analysis)
        setShowStorageAnalysis(true)
        toast.success('Storage analysis complete')
        console.log('Storage Analysis:', data.analysis)
      } else {
        toast.error('Storage analysis failed: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      console.error('Storage analysis error:', error)
      toast.error('Storage analysis failed: ' + error.message)
    } finally {
      setAnalysisLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">🛠️ System Maintenance</h2>
          <p className="text-gray-400">Monitor your video platform and manage all videos</p>
        </div>
        <div className="flex space-x-2">
          <Tooltip text="Smart analysis - Compare R2 storage vs database and identify issues automatically">
            <button
              onClick={analyzeStorage}
              disabled={analysisLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Shield className={`w-4 h-4 ${analysisLoading ? 'animate-pulse' : ''}`} />
              <span>Smart Analysis</span>
            </button>
          </Tooltip>
          <Tooltip text="Show raw R2 storage structure for debugging">
            <button
              onClick={debugR2Storage}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Search className="w-4 h-4" />
              <span>Debug R2</span>
            </button>
          </Tooltip>
          <Tooltip text="Refresh all system data and check for issues">
            <button
              onClick={checkSystemHealth}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Check System Health</span>
            </button>
          </Tooltip>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-white">Database ↔ Storage</h3>
              <Tooltip text="Checks if your video database matches your actual video files. If they don't match, users might see videos that don't work or files that aren't listed." maxWidth="max-w-sm">
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            {systemHealth?.syncStatus === 'synced' ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
            )}
          </div>
          {systemHealth && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Database Records:</span>
                <span className="text-white">{systemHealth.databaseVideos} videos</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Actual Files:</span>
                <span className="text-white">{systemHealth.r2Videos} files</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Status:</span>
                <span className={systemHealth.syncStatus === 'synced' ? 'text-green-400' : 'text-yellow-400'}>
                  {systemHealth.syncStatus === 'synced' ? '✅ Perfect Match' : '⚠️ MISMATCH - Need to Fix'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-white">Storage Used</h3>
              <Tooltip text="How much space your videos are taking up in cloud storage. This affects your monthly costs." maxWidth="max-w-sm">
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <HardDrive className="w-6 h-6 text-blue-500" />
          </div>
          {storageStats && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Total Files:</span>
                <span className="text-white">{storageStats.totalFiles}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Total Size:</span>
                <span className="text-white font-bold">{storageStats.totalSize} GB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Average per File:</span>
                <span className="text-white">
                  {storageStats.totalFiles > 0
                    ? formatBytes(storageStats.totalSizeBytes / storageStats.totalFiles)
                    : '0 B'
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-white">Storage Cleanup</h3>
              <Tooltip text="Broken upload pieces left behind when uploads fail. These waste storage space and money. NOT the same as your video files - these are just digital garbage that should be cleaned up." maxWidth="max-w-sm">
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            {!orphanData?.hasOrphans ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
            )}
          </div>
          <div className="space-y-3">
            <div className="text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Status:</span>
                <span className={!orphanData?.hasOrphans ? 'text-green-400' : 'text-yellow-400'}>
                  {!orphanData?.hasOrphans ? '✅ Clean' : '🗑️ Needs Cleanup'}
                </span>
              </div>
            </div>
            <Tooltip text={!orphanData?.hasOrphans ? "No cleanup needed - system is clean!" : "Clean up broken upload pieces that are wasting storage space."}>
              <button
                onClick={cleanOrphanUploads}
                disabled={loading || !orphanData?.hasOrphans}
                className={`w-full p-2 rounded transition-colors ${
                  !orphanData?.hasOrphans
                    ? 'bg-green-600 text-white cursor-not-allowed opacity-75'
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                } disabled:opacity-50`}
              >
                {!orphanData?.hasOrphans ? '✅ System Clean' : '🗑️ Clean Up Now'}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* R2 Debug Information */}
      {showR2Debug && r2DebugData && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-semibold text-white">🔍 R2 Storage Debug</h3>
              <Tooltip text="Raw storage structure from Cloudflare R2 - useful for debugging file issues">
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <button
              onClick={() => setShowR2Debug(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
            <h4 className="text-blue-300 font-medium mb-2">📋 How to Use This Tool:</h4>
            <div className="text-sm text-gray-300 space-y-1">
              <p><strong>1. Check Storage Summary:</strong> Compare "Total Objects" with your expected video count</p>
              <p><strong>2. Look for Mismatches:</strong> R2 file count should match Supabase database video count</p>
              <p><strong>3. Use "Copy Raw Data":</strong> Copy technical details for support or detailed analysis</p>
              <p><strong>4. Scroll File List:</strong> See all stored video files with sizes and dates</p>
              <p><strong>🚨 Issues to Look For:</strong> Missing files, orphaned files, unexpected file counts</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">Storage Summary</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Objects:</span>
                  <span className="text-white">{r2DebugData.totalObjects}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Video Files:</span>
                  <span className="text-white">{r2DebugData.storagePatterns?.videos?.length || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">Quick Actions</h4>
              <div className="space-y-2">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(r2DebugData, null, 2), 'Debug data')}
                  className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                >
                  Copy Raw Data
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            <h4 className="text-white font-medium mb-3">All Storage Objects:</h4>
            <div className="space-y-2">
              {r2DebugData.allObjects?.map((obj, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded text-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-mono text-xs truncate">{obj.key}</div>
                      <div className="text-gray-400 text-xs mt-1">
                        {formatBytes(obj.size)} • {formatDate(obj.lastModified)}
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(obj.key, 'Object key')}
                      className="p-1 text-gray-400 hover:text-white transition-colors ml-2"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )) || <div className="text-gray-400 text-center py-4">No objects found</div>}
            </div>
          </div>
        </div>
      )}

      {/* Full Video Management List */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <h3 className="text-xl font-semibold text-white">📹 All Videos ({allVideos.length})</h3>
          <Tooltip text="Complete list of all your videos with management tools. Copy IDs, check file sizes, delete directly.">
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
          </Tooltip>
        </div>

        <div className="max-h-96 overflow-y-auto space-y-3">
          {allVideos.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No videos found</p>
            </div>
          ) : (
            allVideos.map((video, index) => (
              <div key={video.id} className="flex items-center justify-between p-4 bg-gray-700 rounded-lg hover:bg-gray-650 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-400 text-sm font-mono">#{(index + 1).toString().padStart(2, '0')}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-white font-medium truncate">{video.title}</h4>
                        {!video.hasFile && (
                          <Tooltip text="File missing from storage!">
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                        <span className="flex items-center space-x-1">
                          <HardDrive className="w-3 h-3" />
                          <span>{formatBytes(video.fileSize)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(video.created_at)}</span>
                        </span>
                        <span className="font-mono text-gray-500">ID: {video.id.substring(0, 8)}...</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Tooltip text="Copy full video ID to clipboard">
                    <button
                      onClick={() => copyToClipboard(video.id, 'Video ID')}
                      className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip text="Delete this video permanently">
                    <button
                      onClick={() => deleteVideo(video)}
                      disabled={loading}
                      className="p-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Smart Storage Analysis Results */}
      {showStorageAnalysis && storageAnalysis && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-semibold text-white">🔍 Smart Storage Analysis</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                storageAnalysis.healthStatus === 'healthy' ? 'bg-green-600 text-white' :
                storageAnalysis.healthStatus === 'warning' ? 'bg-yellow-600 text-white' :
                'bg-red-600 text-white'
              }`}>
                {storageAnalysis.healthStatus === 'healthy' ? '✅ HEALTHY' :
                 storageAnalysis.healthStatus === 'warning' ? '⚠️ ISSUES' :
                 '🚨 CRITICAL'}
              </span>
            </div>
            <button
              onClick={() => setShowStorageAnalysis(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Analysis Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">Database Videos</h4>
              <div className="text-2xl font-bold text-blue-400">{storageAnalysis.summary.databaseVideos}</div>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">R2 Video Files</h4>
              <div className="text-2xl font-bold text-green-400">{storageAnalysis.summary.r2VideoFiles}</div>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-2">Total R2 Objects</h4>
              <div className="text-2xl font-bold text-purple-400">{storageAnalysis.summary.r2TotalObjects}</div>
            </div>
          </div>

          {/* Issues Found */}
          {storageAnalysis.healthStatus !== 'healthy' && (
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-3">🚨 Issues Found:</h4>

              {storageAnalysis.issues.orphanedFiles.length > 0 && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-yellow-300 font-medium">Orphaned Files ({storageAnalysis.issues.orphanedFiles.length})</h5>
                    <button
                      onClick={() => toast.info('Fix button coming soon!')}
                      className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded transition-colors"
                    >
                      🧹 Clean Up
                    </button>
                  </div>
                  <p className="text-yellow-200 text-sm mb-2">Files in R2 storage but not in database (wasting space):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {storageAnalysis.issues.orphanedFiles.map((file, index) => (
                      <div key={index} className="text-xs text-gray-300 font-mono bg-gray-800 p-2 rounded">
                        {file.filename} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {storageAnalysis.issues.missingFiles.length > 0 && (
                <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-red-300 font-medium">Missing Files ({storageAnalysis.issues.missingFiles.length})</h5>
                    <button
                      onClick={() => toast.info('Fix button coming soon!')}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                    >
                      🔧 Fix Links
                    </button>
                  </div>
                  <p className="text-red-200 text-sm mb-2">Database records but files missing from R2 storage:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {storageAnalysis.issues.missingFiles.map((file, index) => (
                      <div key={index} className="text-xs text-gray-300 bg-gray-800 p-2 rounded">
                        <div className="font-medium">{file.title || 'Untitled'}</div>
                        <div className="text-gray-400">{file.filename} ({file.cloudflareUid || 'No CloudflareUID'})</div>
                        <div className="text-gray-500">ID: {file.videoId}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {storageAnalysis.issues.duplicateFiles.length > 0 && (
                <div className="bg-orange-900/30 border border-orange-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-orange-300 font-medium">Duplicate Files ({storageAnalysis.issues.duplicateFiles.length})</h5>
                    <button
                      onClick={() => toast.info('Fix button coming soon!')}
                      className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded transition-colors"
                    >
                      🔄 Deduplicate
                    </button>
                  </div>
                  <p className="text-orange-200 text-sm mb-2">Multiple files with same name:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {storageAnalysis.issues.duplicateFiles.map((dup, index) => (
                      <div key={index} className="text-xs text-gray-300 bg-gray-800 p-2 rounded">
                        <div className="font-medium">{dup.filename} ({dup.count} copies)</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* All Clear Message */}
          {storageAnalysis.healthStatus === 'healthy' && (
            <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-6 text-center">
              <div className="text-4xl mb-2">🎉</div>
              <h4 className="text-green-300 font-medium mb-2">Storage is Healthy!</h4>
              <p className="text-green-200 text-sm">
                R2 storage and database are perfectly synchronized.
                No orphaned files, missing files, or duplicates found.
              </p>
            </div>
          )}

          <div className="text-xs text-gray-400 mt-4">
            Analysis completed: {new Date(storageAnalysis.summary.analysisDate).toLocaleString()}
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <RefreshCw className="w-6 h-6 text-white animate-spin" />
            <span className="text-white">Working on it...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default MaintenanceTab